import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Flame, GitFork, Scale, Activity, FileQuestion, TrendingUp, Clock, Sparkles } from "lucide-react";
import { useStore } from "@/store/useStore";
import { StrainCard } from "@/components/StrainCard";
import { Button } from "@/components/Button";
import { EmptyState } from "@/components/AppShell";
import { cn } from "@/lib/cn";

type Filter = "new" | "active" | "forked" | "contested" | "mind_changed" | "needs_evidence" | "rising";

const FILTERS: { key: Filter; label: string; icon: React.ReactNode }[] = [
  { key: "new", label: "New", icon: <Clock size={13} /> },
  { key: "active", label: "Active", icon: <Flame size={13} /> },
  { key: "forked", label: "Most forked", icon: <GitFork size={13} /> },
  { key: "contested", label: "Most contested", icon: <Scale size={13} /> },
  { key: "mind_changed", label: "Minds moved", icon: <Activity size={13} /> },
  { key: "needs_evidence", label: "Needs evidence", icon: <FileQuestion size={13} /> },
  { key: "rising", label: "Rising", icon: <TrendingUp size={13} /> },
];

export default function Feed() {
  const strains = useStore((s) => s.strains);
  const loadStrains = useStore((s) => s.loadStrains);
  const [filter, setFilter] = useState<Filter>("new");

  useEffect(() => {
    loadStrains(filter);
  }, [filter, loadStrains]);

  const sorted = useMemo(() => {
    const arr = [...strains];
    switch (filter) {
      case "new": return arr.sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
      case "active": return arr.sort((a, b) => +new Date(b.activity?.last_activity ?? b.updated_at) - +new Date(a.activity?.last_activity ?? a.updated_at));
      case "forked": return arr.sort((a, b) => (b.activity?.forks ?? 0) - (a.activity?.forks ?? 0));
      case "contested": return arr.sort((a, b) => (b.contention_score ?? 0) - (a.contention_score ?? 0));
      case "mind_changed": return arr.sort((a, b) => (b.activity?.mind_changes ?? 0) - (a.activity?.mind_changes ?? 0));
      case "needs_evidence": return arr.filter((s) => s.status_badges?.includes("needs_evidence")).concat(arr.filter((s) => !s.status_badges?.includes("needs_evidence")));
      case "rising": return arr.filter((s) => s.status_badges?.includes("rising")).concat(arr.filter((s) => !s.status_badges?.includes("rising")));
    }
  }, [strains, filter]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">The feed</h1>
          <p className="text-[13px] text-[var(--color-ink-muted)] mt-0.5">A gallery of idea artifacts — not a leaderboard of takes.</p>
        </div>
        <Link to="/create">
          <Button leftIcon={<Plus size={15} />}>New strain</Button>
        </Link>
      </div>

      {/* filters */}
      <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-3 mb-4 border-b border-[var(--color-border)]">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={cn(
              "inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[13px] whitespace-nowrap transition-colors",
              filter === f.key
                ? "bg-[var(--color-surface-3)] text-[var(--color-ink)]"
                : "text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] hover:bg-[var(--color-surface-2)]",
            )}
          >
            {f.icon}
            {f.label}
          </button>
        ))}
      </div>

      {/* grid */}
      {sorted.length === 0 ? (
        <EmptyState
          icon={<Sparkles size={28} />}
          title="Nothing here yet"
          desc="Create the first strain in this view."
          action={<Link to="/create"><Button leftIcon={<Plus size={15} />}>Create a strain</Button></Link>}
        />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sorted.map((s, i) => <StrainCard key={s.id} strain={s} index={i} />)}
        </div>
      )}
    </div>
  );
}
